import type { Dictionary } from './fr';

/**
 * Dictionnaire CHINOIS (SIMPLIFIÉ).
 *
 * ⚠️ Traduction automatique relue par IA (Claude, 17/10/2026), pas par un locuteur natif.
 *
 * Registre : chinois simplifié standard (Chine continentale), adresse directe en « 你 », ton simple.
 * Glossaire : alternance → 工学交替 ; stage → 实习 ; CDI / CDD → sigle toujours gardé, suivi d'une glose :
 * CDI（无固定期限合同）/ CDD（固定期限合同）, formes courtes CDI（无固定期限）/ CDD（固定期限）;
 * CV → 简历 ; lettre de motivation → 求职信 ; entretien → 面试 ; IA → AI ; recruteur → 招聘官 ;
 * offre → 职位 ; fiche de poste → 职位描述 ; annonce → 招聘启事 ; candidature IA → AI 申请 ;
 * notation → 评分 ; audit LinkedIn → LinkedIn 诊断 ; coaching → 辅导 ; plan → 套餐 ; école → 学校 ;
 * CFA → sigle gardé : 学徒培训中心（CFA）; template → 模板. Noms de plans (Free, Plus, Pro) et marques non traduits.
 */
export const zh: Dictionary = {
  common: {
    back: '返回',
    scoreSur100: '满分 100',
    cancel: '取消',
    save: '保存',
    retry: '重试',
    delete: '删除',
    ok: '我知道了',
  },

  a11y: {
    close: '关闭',
    removeLanguage: '移除语言',
    removeBullet: '移除此要点',
    removeProject: '移除项目',
    removeEducation: '移除教育经历',
    removeExperience: '移除工作经历',
    removeTag: '移除%{tag}',
    useful: '有帮助',
    notUseful: '没有帮助',
    listen: '收听此消息',
    notifications: '通知',
    settings: '设置',
    changePhoto: '更换头像',
    changeSearchTarget: '更改你的搜索目标',
    importPhoto: '导入照片',
    score: '得分：%{score}/100',
  },

  nav: {
    carriere: '职业',
    offers: '职位',
    news: '资讯',
    profile: '我的',
    aiAction: 'AI 操作',
  },

  home: {
    content: {
      seeAll: '查看全部',
      new: '最新',
      duration: '%{minutes} 分钟',
      play: '收听',
      types: {
        podcast: 'AlternJob 播客',
        tip: '每日建议',
        event: '不容错过',
        success: '他们成功了',
        market: '市场与职位',
        masterclass: '大师课',
        challenge: '挑战',
        news: 'Horméia 新功能',
      },
      sample: {
        title: 'AI 与招聘：2024 年真正改变了什么',
        description: '求职市场的建议、经验分享与趋势。',
      },
    },
  },


  carriere: {
    title: '职业',
    headline: '成为让招聘官\n主动来电的',
    headlineAccent: '候选人。',
    position: '第 %{index} 张卡片，共 %{total} 张',
    cv: {
      title: 'ATS 简历',
      description:
        '打造一份专业简历，顺利通过筛选系统，吸引招聘官的注意。',
      cta: '分析我的简历',
    },
    linkedin: {
      title: 'LinkedIn',
      description:
        '打造专业的个人主页，提升你在招聘官和企业面前的曝光度。',
      cta: '优化我的主页',
    },
    letter: {
      title: '求职信',
      description: '为每一次申请写出有说服力、量身定制的求职信。',
      cta: '写我的求职信',
    },
    interview: {
      title: 'AI 面试',
      description: '多加练习，拿下每一场面试。',
      cta: '开始练习',
    },
    // Page Carrière sur grand écran : panneau « prochain pas », chiffres et état de chaque outil.
    tableau: {
      prochainPas: '拿到面试的下一步',
      premierPas: '先了解自己的现状',
      conseilParDefaut: '你的简历还有提升空间：打开详情查看修改建议。',
      sansScore: '上传你的简历：几秒钟内获得 ATS 评分和最关键的修改建议。',
      corriger: '修改我的简历',
      noter: '给我的简历打分',
      chiffreCv: { one: '已保存简历', other: '已保存简历' },
      chiffreLettres: { one: '已写求职信', other: '已写求职信' },
      chiffreCorrections: { one: '修改建议', other: '修改建议' },
      statutCvAucun: '还没有简历',
      statutCv: { one: '%{count} 份简历', other: '%{count} 份简历' },
      statutScore: '评分 %{score}',
      statutLettreAucune: '还没有求职信',
      statutLettres: { one: '已写 %{count} 封求职信', other: '已写 %{count} 封求职信' },
      statutLinkedin: '一分钟完成诊断',
      statutInterview: '招聘官或经理',
    },
  },

  offers: {
    title: '职位',
    subtitle: '很快，你将看到根据你的履历和搜索推荐的职位。',
    soonTitle: '新功能开发中',
    soonBody:
      '我们正在准备一批与你的简历和求职方向相匹配的工学交替（alternance）职位和首份工作机会。在此之前，你可以在“职业”页面完善你的简历、LinkedIn 主页和求职信。',
  },

  offres: {
    franceUniquement: '职位仅限法国境内的岗位，且均以法语撰写。',
    titre: '职位',
    sousTitre: '根据你的条件挑选',
    credits: { zero: '本月 AI 申请次数已用完', one: '剩余 %{count} 次 AI 申请', other: '剩余 %{count} 次 AI 申请' },
    segments: { pourToi: '为你推荐', sauvegardees: '已收藏' },
    modifierCriteres: '修改我的条件',
    sources: { france_travail: 'France Travail', lba: 'La bonne alternance', adzuna: 'Jobs by Adzuna' },
    contrats: { alternance: '工学交替', stage: '实习', cdi: 'CDI（无固定期限合同）', cdd: 'CDD（固定期限合同）', autre: '其他合同' },
    contratsEnLigne: { alternance: '工学交替', stage: '实习', cdi: 'CDI（无固定期限）', cdd: 'CDD（固定期限）', autre: '其他合同' },
    rythmes: {
      '3j2j': '3天 / 2天',
      '4j1j': '4天 / 1天',
      '1sem1sem': '1周 / 1周',
      '2sem2sem': '2周 / 2周',
      '3sem1sem': '3周 / 1周',
      '2j3j': '2天 / 3天',
    },
    rythmesComplets: {
      '3j2j': '企业3天，学校2天',
      '4j1j': '企业4天，学校1天',
      '1sem1sem': '企业1周，学校1周',
      '2sem2sem': '企业2周，学校2周',
      '3sem1sem': '企业3周，学校1周',
    },
    dureesStage: { '2': '2个月', '3': '3个月', '4': '4个月', '5': '5个月', '6': '6个月' },
    dureesCdd: { lt6: '少于6个月', '6a12': '6至12个月', '12plus': '1年及以上' },
    rayonKm: '%{km} 公里',
    preferences: {
      titre: '你的条件',
      sousTitre: '我们会根据你在这里的选择来搜索。',
      contratMulti: '可多选',
      peuImporte: '都可以',
      cdiSeul: '选择无固定期限合同（CDI）时，无需再选择其他内容。',
      verifier: '请检查上方标出的字段。',
      metier: '目标职业',
      metierPlaceholder: '例如：chargé·e de communication digitale',
      contrat: '合同类型',
      ville: '城市',
      villePlaceholder: '例如：Lyon',
      rayon: '范围',
      rythme: '工学交替节奏',
      rythmeAide: '企业 / 学校',
      dureeStage: '实习时长',
      dureeCdd: '定期合同时长',
      dureeAide: '标明其他时长的职位不会显示。',
      reponsesTitre: '关键回答，绝不编造',
      permis: 'B 类驾照',
      oui: '有',
      non: '没有',
      aRenseigner: '待填写',
      disponibilite: '最早到岗日期',
      disponibilitePlaceholder: 'DD/MM/YYYY',
      voir: '查看我的职位',
      metierRequis: '请填写你的目标职业。',
      villeRequise: '请填写你的城市。',
      villeIntrouvable: '在法国找不到这个城市。请检查拼写，或选择一个法国城市。',
      villeRetenue: '识别到的城市：%{commune}。如果不对，请修改；如果正确，请再次确认。',
      contratRequis: '请至少选择一种合同类型。',
      disponibiliteInvalide: '请按 DD/MM/YYYY（日/月/年）格式填写日期。',
      erreurEnregistrement: '无法保存你的条件，请重试。',
      chargement: '正在加载你的条件…',
      retour: '返回职位',
    },
    raisons: {
      contrat: '合同：%{valeur}',
      contrat_different: '合同类型与你的搜索不同',
      distance: '距你的城市 %{valeur} 公里',
      distance_loin: '距离 %{valeur} 公里，超出你的范围',
      distance_inconnue: '工作地点未注明',
      distance_rayon: '在你的 %{valeur} 公里范围内',
      rythme: '节奏 %{valeur}，符合你的期望',
      rythme_different: '节奏 %{valeur}，与你的不同',
      rythme_absent: '工学交替节奏未注明',
      metier: '职位名称与你的目标职业相近：“%{valeur}”',
      metier_partiel: '职位名称部分相近：“%{valeur}”',
      metier_rome: '与你的职业属于同一职业类别（ROME 代码 %{valeur}）',
      metier_eloigne: '职位名称与你的目标职业相差较远',
      metier_sans_citation: '符合你的目标职业',
      metier_partiel_sans_citation: '与你的目标职业相近',
      permis_a_renseigner: '要求 B 类驾照：待填写',
      permis_manquant: '要求 B 类驾照',
    },
    carte: {
      pourquoi: '为什么匹配',
      publiee: { one: '%{source} · %{jours} 天前发布', other: '%{source} · %{jours} 天前发布' },
      publieeAujourdhui: '%{source} · 今天发布',
      voirDetail: '查看详情',
      voirOffreComplete: '查看完整职位',
      contratDuree: '%{contrat} · %{duree}',
      dureeMois: { one: '%{count} 个月', other: '%{count} 个月' },
      salaire: '薪资：%{valeur}',
      entrepriseInconnue: '企业未公开',
      lieuDistance: '%{lieu} · %{km} 公里',
      score: '匹配分数：%{score}/100',
    },
    actions: {
      passer: '跳过这个职位',
      sauvegarder: '收藏这个职位',
      interesse: '我对这个职位感兴趣',
      aide: '感兴趣就向右滑，不感兴趣就向左滑',
      libelles: { passer: '跳过', sauvegarder: '收藏', interesse: '感兴趣' },
    },
    detail: {
      voirCandidature: '查看我的申请',
      alerteMineur: '请核实：该职位似乎涉及%{motifs}，未满18岁者禁止或受限。',
      motifsMineur: { nuit: '夜间工作', alcool: '酒类服务', hauteur: '高空作业' },
      fermer: '关闭详情',
      monMatch: '你的匹配度',
      description: '职位描述',
      extrait: '招聘启事摘录',
      extraitAide: 'Adzuna 只提供招聘启事的开头部分，完整内容请到其网站查看。',
      voirOffreComplete: '查看完整职位',
      entreprise: '企业介绍',
      infos: '信息',
      voirOffre: '查看原始职位',
      preparer: '准备我的申请',
      source: '来源：%{source}',
      majLe: '更新于 %{date}',
      licence: '转载须遵守 France Travail 许可协议',
      retiree: '该职位已被招聘方撤下。',
      logo: '%{entreprise} 的标志',
      sauvegardee: '职位已收藏',
      rubriques: {
        nature_contrat: '合同性质',
        type_contrat: '合同类型',
        duree_travail: '工作时长',
        experience: '经验',
        formations: '学历',
        competences: '技能',
        qualites: '职业素质',
        savoirs: '工具',
        langues: '语言',
        permis: '驾照',
        salaire: '薪资',
        secteur: '行业',
        qualification: '资质等级',
        deplacements: '出差',
        conditions: '工作条件',
        postes: '招聘人数',
        accessible_th: '残障人士可申请',
        contact: '联系方式',
        agence: 'France Travail 办事处',
        duree_contrat: '合同期限',
        debut: '合同开始日期',
        expiration: '职位有效期至',
        rome: 'ROME 代码',
      },
    },
    preparation: {
      parallele: '同时进行',
      motsClesTitre: '职位中识别出的关键词',
      motsClesAutres: '+%{count}',
      motsClesA11y: { one: '识别出 1 个关键词：%{liste}', other: '识别出 %{count} 个关键词：%{liste}' },
      motsClesCouvertsA11y: '你的简历覆盖了 %{total} 个关键词中的 %{couverts} 个：%{liste}',
      titre: '你的 AI 正在准备申请',
      pret: '你的申请已准备好',
      pour: '申请 %{entreprise} · %{poste}',
      pourPoste: '申请 %{poste}',
      fermer: '关闭准备',
      terminer: '关闭',
      verifier: '检查我的申请',
      reessayer: '重试',
      retourOffres: '返回职位',
      pied: '未经你确认，什么都不会发出',
      progression: '申请准备进度',
      etapes: { analyse: '分析职位', cv: '针对职位调整简历', lettre: '求职信', controle: '质量检查' },
      attente: '等待中',
      enCours: '进行中…',
      echecEtape: '这一步失败了',
      motsCles: { zero: '无关键词', one: '1 个关键词', other: '%{count} 个关键词' },
      questions: { zero: '无问题', one: '1 个问题', other: '%{count} 个问题' },
      analyseFaite: '识别结果：%{motsCles}，%{questions}',
      cvFait: { zero: '未融入任何关键词', one: '已融入 1 个关键词', other: '已融入 %{count} 个关键词' },
      titreAligne: '，标题已与职位对齐',
      lettreFaite: '求职信共 %{count} 词',
      controleFait: { zero: '没有需要确认的内容', one: '有 1 处需要确认', other: '有 %{count} 处需要确认' },
      couverture: '关键词覆盖：%{couverts}/%{total}',
      erreurs: {
        quota: '你本月的 AI 申请次数已全部用完。',
        cv_manquant: '准备申请需要一份主简历。请在“职业”页面创建或导入你的简历。',
        offre_retiree: '该职位已下线，无法准备申请。',
        profil_trop_eloigne: '该职位与你的简历差距太大，无法写出可信的求职信。',
        echec: '有一步失败了。请重试：已完成的步骤会保留。',
      },
    },
    avant: {
      fermer: '关闭',
      naissanceTitre: '你的出生日期',
      naissanceRaison: '它用于调整求职信的语气，并在职位不允许未满18岁者从事时提醒你。只需填写一次。',
      naissanceFormat: 'DD/MM/YYYY',
      erreurNaissance: '你的出生日期未能保存，请重试。',
      continuer: '继续',
      annuler: '取消',
      eloigneeTitre: '这个职位与你的履历差距较大',
      eloigneeTexte: '你的匹配度为 %{score}/100。你仍然可以准备申请：如果准备成功，将消耗 1 次额度。',
      prepareQuandMeme: '仍然准备',
      nePasPreparer: '不准备',
    },
    relecture: {
      titre: '检查你的申请',
      fermer: '关闭检查',
      erreurLecture: '你的申请未能加载。',
      reessayer: '重试',
      manquants: '你的简历中缺少：%{liste}',
      aConfirmer: '发送前请核实',
      raisons: {
        nombre: '你的简历中没有这个数字',
        date: '你的简历中没有这个日期',
        competence: '你的简历中未体现这项技能',
        a_confirmer: '待确认',
      },
      exact: '是的，没错',
      corriger: '修改',
      enregistrer: '保存',
      annuler: '取消',
      champCorrection: '你的修改',
      confirme: '已确认',
      corrige: '已修改',
      erreurCorrection: '这处修改未能保存，请重试。',
      onglets: { cv: '简历', lettre: '求职信', reponses: '回答' },
      legende: '高亮部分：AI 针对该职位修改的内容。',
      formation: '教育背景',
      competences: '技能',
      langues: '语言',
      objet: '事由：',
      copierLettre: '复制求职信',
      erreurCopie: '无法打开分享，请重试。',
      reponses: {
        oui: '是',
        non: '否',
        date: '%{date} 起可到岗',
        age: '%{age} 岁',
        aRenseigner: '待填写',
        aucune: '该职位中未发现淘汰性问题。',
      },
      exporterCv: '导出简历（PDF）',
      exporterLettre: '导出求职信（PDF）',
      erreurExport: '导出失败，请重试。',
      voirOffre: '查看职位',
      voirOffreComplete: '查看完整职位',
      extrait: '本申请基于招聘启事摘录准备：发送前请阅读完整职位信息。',
    },
    etats: {
      chargement: '正在为你搜索职位…',
      erreurTitre: '无法加载职位',
      erreurTexte: '请检查网络连接后重试。',
      reessayer: '重试',
      videTitre: '没有符合这些条件的职位',
      videTexte: '扩大范围，或试试相近的职业。',
      videTexteDuree: '扩大范围，试试相近的职业，或放宽合同时长。',
      epuiseTitre: '目前的职位你都看完了',
      epuiseTexte: '稍后再来，或放宽条件查看更多职位。',
      sourcePartielle: '某个职位来源暂无响应：列表可能不完整。',
      sauvegardeesVideTitre: '还没有收藏的职位',
      sauvegardeesVideTexte: '点击职位上的书签图标，就能在这里找到它。',
      retiree: '职位已撤下',
      decisionEchec: '你的选择未能保存，请检查网络连接。',
    },
  },

  news: {
    title: '资讯',
    segments: { forYou: '为你推荐', favorites: '收藏' },
    verified: '认证发布者',
    readingTime: '阅读约 %{count} 分钟',
    relative: {
      today: '今天',
      yesterday: '昨天',
      days: { one: '%{count} 天前', other: '%{count} 天前' },
      weeks: { one: '%{count} 周前', other: '%{count} 周前' },
      months: { one: '%{count} 个月前', other: '%{count} 个月前' },
      years: { one: '%{count} 年前', other: '%{count} 年前' },
    },
    like: {
      add: '点赞这篇文章',
      remove: '取消点赞',
      count: {
        zero: '还没有人点赞',
        one: '%{count} 个赞',
        other: '%{count} 个赞',
      },
      failed: '无法更新点赞。',
    },
    favorite: {
      add: '加入收藏',
      remove: '取消收藏',
      added: '已收藏',
      notAdded: '未收藏',
      failed: '无法更新收藏。',
    },
    errorTitle: '资讯加载失败',
    errorBody: '请检查网络连接后重试。',
    emptyTitle: '暂时还没有文章',
    emptyBody: '新文章发布后会第一时间出现在这里。',
    favoritesEmptyTitle: '还没有收藏',
    favoritesEmptyBody: '点击文章上的书签图标，就能在这里找到它。',
    favoritesSignedOutTitle: '登录后即可保存文章',
    favoritesSignedOutBody: '你的收藏与账号绑定，在不同设备间同步。',
    end: '你已经看完所有内容了。',
  },

  notifications: {
    welcome: {
      title: '欢迎来到 Horméia 👋',
      body: '先生成一份 ATS 优化简历，再和 AI 一起练习面试。两步走，拿到更多面试机会。',
    },
    quantify: {
      title: '用数字说话',
      body: '在简历上，把“负责社交媒体运营”换成“3个月内互动量提升35%”。招聘官和 ATS 筛选系统都更看重数字。',
    },
    followUp: {
      title: '7天后跟进',
      body: '投递一周后仍未收到回复，就给招聘官发一条简短、礼貌的消息：恰当的跟进往往能让回复几率翻倍。',
    },
  },

  myCv: {
    title: '我的简历',
    scoreLabel: '你的 ATS 评分',
    scoreNone: '先做一次评分，看看你的 ATS 分数。',
    scoreClean: '没有待优化的项目，你的简历已准备就绪。',
    scoreAdvice: {
      one: '还有 %{count} 项优化可以提高你的成功率。',
      other: '还有 %{count} 项优化可以提高你的成功率。',
    },
    scoreActionFirst: '给我的简历评分',
    scoreActionDetail: '查看详情',
    createOrImport: '创建或导入',
    importTitle: '导入我的简历',
    importSubtitle: 'PDF 或 Word',
    createTitle: '创建简历',
    createSubtitle: '从模板开始',
    improve: '优化我的简历',
    scoreRowTitle: '简历评分',
    scoreRowSubtitle: 'ATS 评分和优先事项',
    optimiseTitle: '优化我的简历',
    optimiseSubtitle: '改进现有简历',
    myCvs: '我的简历',
    savedCount: {
      zero: '还没有保存的简历',
      one: '已保存 %{count} 份简历',
      other: '已保存 %{count} 份简历',
    },
    savedListOpen: '查看我保存的简历',
    listSubtitle: '查找、打开和管理你的简历',
    priorityLabel: '优先修改',
    importShort: '导入',
    createShort: '创建简历',
    cvNote: '已评分的简历',
    changer: '更换',
  },

  cvList: {
    title: '我的简历',
    subtitle: '查找、打开和管理你保存的简历。',
    loadError: '无法加载你的简历，请重试。',
    emptyTitle: '暂时还没有简历。',
    emptyText: '几分钟就能创建你的第一份 ATS 优化简历。',
    emptyCta: '创建我的简历',
    primary: '主简历',
    setPrimary: '设为主简历',
    edit: '编辑',
    preview: '预览',
    others: '我的其他简历',
    score: 'ATS 评分 %{score}',
    more: '更多操作',
    deleteTitle: '删除这份简历？',
    deleteBody: '“%{title}”将被永久删除。',
  },

  cvImprove: {
    title: '优化我的简历',
    subtitle: 'AI 会提出具体的修改建议，你可以只保留想要的。',
    loading: 'AI 正在分析你的简历…',
    blocked: '此功能仅限订阅用户使用。',
    notFound: '找不到简历。请返回评分页面后重试。',
    saveFailed: '无法保存。',
    reponseInvalide: '返回的结果不完整，请稍后再试。',
    defaultTitle: '优化后的简历',
    choixQuestion: '要不要导入一份职位描述，让你的简历与该职位更匹配？',
    choixDeja: '你评分时使用了职位描述。要用它来优化简历吗？',
    choixNon: '不用，直接优化',
    choixOui: '是，添加职位描述',
    choixUtiliser: '使用已导入的职位描述',
    ficheTitre: '职位描述',
    ficheSousTitre: '你的简历会针对这个职位进行调整，绝不编造任何内容。',
    ficheChamp: '截图、PDF 或文本',
    ficheAide: '可以组合使用：它们会被当作同一个职位来读取。最多 5 张截图、总计 8 MB，PDF 最多 20 页。',
    optimiser: '优化我的简历',
    passer: '跳过',
    ficheUtilisee: '已根据所提供的职位描述进行调整。',
    // Optimisation par modifications (1.2.0).
    revue: {
      titre: {
        one: '建议修改 %{count} 处',
        other: '建议修改 %{count} 处',
      },
      aide: '逐条接受或拒绝修改。在你点击应用之前，简历不会有任何变化。',
      aideTitre: '逐条接受或拒绝修改。',
      aideSuite: '在你点击应用之前，简历不会有任何变化。',
      // Libellé SEUL, posé à côté du nombre affiché dans sa tuile (le titre complet reste `titre`).
      libelle: { one: '处建议修改', other: '处建议修改' },
      score: '修改后的简历不会重新评分：之后请评分以查看 ATS 评分。',
    },
    tags: { impact: '影响力', action: '行动动词', keywords: '关键词', clarity: '清晰度', title: '标题' },
    modif: { avant: '修改前', apres: '修改后', appliquer: '应用：%{nom}', titreCv: '简历标题' },
    pied: {
      retenues: '已保留：%{count} / %{total}',
      appliquer: {
        one: '应用 %{count} 处修改',
        other: '应用 %{count} 处修改',
      },
      aucune: '未选择任何修改',
    },
    vide: { titre: '你的简历已经很扎实', detail: 'AI 没有发现值得提出的修改。你的额度没有被扣除。' },
    scoreInconnu: { titre: '评分未测量', detail: '给这份简历评分，查看它的 ATS 评分。' },
  },

  ficheDePoste: {
    choisirOffre: '选择一个职位',
    chargement: '正在加载你的职位…',
    erreur: '无法加载你收藏的职位。',
    vide: '还没有收藏的职位。在“职位”页面收藏一个职位，就能在这里找到它。',
    coller: '粘贴完整的招聘启事',
    compteur: '%{n} / %{max} 字符',
    retirerCapture: '移除第 %{n} 张截图',
    retirerFichier: '移除文件',
    erreurs: {
      tropDeCaptures: '最多 %{max} 张截图。',
      captureIllisible: '有一张截图无法读取，请换一张图片。',
      tropLourd: '截图总大小超过 8 MB。请移除一张后继续。',
      pdfTropLong: '该 PDF 超过 20 页。请只保留职位描述部分。',
    },
  },

  cvScore: {
    failed: '评分失败，请重试。',
    title: '给你的简历评分',
    targetRoleLabel: '目标职位',
    targetRoleHint: '你想申请的职位：AI 也会评估你的简历与该职位的匹配度。',
    targetRolePlaceholder: '例如：网页开发（工学交替）',
    offerLabel: '职位描述',
    offerHint: '添加职位信息，获得匹配度评分和针对该职位的建议。',
    offerPlaceholder: '在这里粘贴职位信息…',
    offerFile: '职位（PDF/Word）',
    offerCaptures: '截图',
    optional: '选填',
    ficheAdded: '已添加',
    moreAccurate: '获得更精确的评分',
    frise: { import: '导入', evalue: 'AI 评估', score: '你的评分' },
    dropTitle: '放入你的简历',
    dropSub: 'PDF 或 Word。你会得到 ATS 评分和具体建议。',
    dropReady: '简历已就绪，可以评分',
    dropTextReady: '你的简历文本已就绪。',
    dropPick: '选择文件',
    dropChange: '更换文件',
    hintEmpty: '导入简历即可获得你的 ATS 评分。',
    hintReady: '已就绪：几秒钟后你就能看到评分。',
    submit: '给我的简历评分',
    atsScore: 'ATS 评分',
    offerMatch: '与职位的匹配度',
    strengths: '你在该职位上的优势',
    gaps: '与职位的差距',
    saveImported: '保存到“我的简历”',
    saveFailed: '无法保存，请重试。',
    importedTitle: '导入的简历',
    improve: '优化我的简历',
    verdict: { faible: '需要改进', correct: '尚可，仍有提升空间', bon: '不错的简历，稍作调整即可', excellent: '非常出色，可以投递' },
    resultDetail: {
      zero: '暂无待优化项。',
      one: '还有 %{count} 项优化可以提高你的成功率。',
      other: '还有 %{count} 项优化可以提高你的成功率。',
    },
    advicePrio: '优先修改',
    rescore: '重新评分',
    rescoreTitre: '重新评分？',
    rescoreMessage: '你将返回表单，当前显示的评分会丢失。',
    rescoreAnnuler: '保留评分',
    criteres: { titre: '分项评分', keywords: '关键词', layout: 'ATS 排版', impact: '量化成果', readability: '可读性' },
  },

  cvCreate: {
    questions: {
      role: { label: '目标职位', placeholder: '例如：项目专员' },
      education: {
        label: '教育背景、学位和证书',
        placeholder: '例如：市场营销硕士一年级，IAE Paris',
      },
      experiences: {
        label: '工作经历（实习、工学交替、兼职）',
        placeholder: '描述你的工作经历',
      },
      projects: {
        label: '个人项目（个人、社团、业余项目）',
        placeholder: '描述你的个人项目',
      },
      skills: { label: '技能和工具', placeholder: '例如：SQL、SEO、Excel、Figma' },
      languages: { label: '语言' },
      interests: {
        label: '兴趣爱好',
        placeholder: '例如：越野跑、摄影、国际象棋',
      },
      extra: {
        label: '还有什么要补充的吗？（选填）',
        placeholder: '自由填写，AI 会把它放在合适的位置',
      },
    },
    steps: {
      analyse: '分析你的回答',
      format: '简历排版',
      ats: 'ATS 检查',
    },
  },

  languagePicker: {
    namePlaceholder: '语言',
    level: '水平',
    diploma: '证书（选填）',
    diplomaPlaceholder: '例如：TOEFL 105（或留空）',
    add: '添加语言',
  },

  editors: {
    experience: {
      titre: '工作经历',
      rowTitle: '经历 %{n}',
      roleLabel: '职位',
      end: '结束',
      descriptionLabel: '你做了什么',
      descriptionPlaceholder: '简单写就好，AI 会帮你润色。',
      role: '职位名称',
      company: '企业',
      start: '开始',
      current: '当前职位',
      description: '你做了什么（AI 会帮你改写）',
      add: '添加经历',
    },
    education: {
      titre: '教育背景',
      rowTitle: '学历 %{n}',
      degree: '学位 / 名称',
      school: '学校',
      date: '日期',
      current: '在读',
      add: '添加教育经历',
    },
    project: {
      titre: '项目',
      rowTitle: '项目 %{n}',
      descriptionLabel: '你完成了什么',
      descriptionPlaceholder: '简单写就好，AI 会帮你润色。',
      name: '项目名称',
      date: '日期',
      current: '进行中',
      description: '你完成了什么（AI 会帮你改写）',
      add: '添加项目',
    },
    bullets: {
      placeholder: '描述一项成果 / 一个行动',
      add: '添加要点',
    },
    template: {
      groupLabel: '简历模板',
      mono: '专业',
      accent: '彩色',
      accentColor: '强调色',
      couleur: {
        lime: '青柠绿',
        vert: '绿色',
        bleu: '蓝色',
        beige: '米色',
        marron: '棕色',
        ardoise: '石板灰',
        marine: '藏青色',
      },
    },
    quantifyHint: '记得用数字量化你的成果（多少、百分比、什么影响），能大幅提升你的 ATS 评分。',
    searchTarget: '你在找…',
  },

  cvEditor: {
    before: '修改前',
    after: '修改后 - AI 建议',
    reject: '拒绝',
    accept: '接受',
    improveWithAi: '用 AI 优化',
    headline: '简历标题',
    experiences: '工作经历',
    projects: '项目',
    education: '教育背景',
    skills: '技能',
    languages: '语言',
    interests: '兴趣爱好',
    headlinePlaceholder: '例如：AI Ambassador & Community Coordinator',
    startPlaceholder: '例如：2024年1月',
    endPlaceholder: '例如：至今',
    projectDatePlaceholder: '例如：2024年3月',
    educationDatePlaceholder: '例如：2025年9月',
    skillPlaceholder: '例如：SQL',
    languagePlaceholder: '例如：英语 - C1',
    interestPlaceholder: '例如：越野跑',
    experienceLabel: '经历',
    projectLabel: '项目',
    startLabel: '开始',
    endLabel: '结束',
    dateLabel: '日期',
    rewriteRetry: '请重试。',
  },

  qualityCheck: {
    title: '优化了哪些内容',
    corrections: '修正',
    languagesRemoved: '已移除的语言（低于 B2 水平）',
    skillsNotProven: '职位要求但未体现的技能',
    toValidate: '需要你确认',
    removed: '已删除的内容',
    recommendations: '建议',
  },

  linkedin: {
    sousScores: { titre: '分项评分', title: '标题', about: '简介', experiences: '工作经历' },
    kit: {
      failed: '优化工具包未能生成。你的评分和优先事项已保留。',
      retry: '重新生成工具包',
    },
    title: 'LinkedIn 诊断',
    subtitle: 'AI 会读取你的主页，告诉你该改进什么。',
    verdict: { faible: '资料需要加强', correct: '资料尚可，仍有提升空间', bon: '不错的资料，稍作调整即可', excellent: '资料非常出色' },
    resultDetail: {
      zero: '暂无优先事项。',
      one: '有 %{count} 项优先改进。',
      other: '有 %{count} 项优先改进。',
    },
    profilAnalyse: '资料已分析',
    relancer: '重新审核',
    relancerTitre: '重新审核？',
    relancerMessage: '你将回到表单页面，当前显示的审核结果和套件将丢失。',
    relancerAnnuler: '保留此次审核',
    languageLabel: '诊断语言',
    languageHint: '你的主页保持原有语言：诊断报告和工具包会用这里选择的语言撰写。',
    addCaptures: '添加截图',
    analyse: '分析我的主页',
    sourceConnect: 'LinkedIn 账号',
    sourceShots: '截图',
    capturesGras: '最多 5 张截图',
    capturesSuite: '，请按你希望被阅读的顺序排列。',
    captureRemove: '移除第 %{n} 张截图',
    profil: { titre: '标题', apropos: '简介', experiences: '经历' },
    promesses: { audit: '详细的诊断', kit: '可复制的工具包' },
    hint: { empty: '至少添加一张截图才能开始诊断。', ready: '一切就绪，可以开始诊断了。', connect: '连接你的 LinkedIn 后即可开始诊断。', account: '将直接从你的账号读取主页。' },
    missingKeywords: '建议添加的关键词',
    priorities: '优先行动',
    connection: {
      secureTitle: '安全连接',
      secureBody: '我们只获取分析你的主页所必需的信息。',
      connected: 'LinkedIn 已连接',
      connectedSub: '安全连接，无需你的密码。',
      expired: 'LinkedIn 会话已过期。请重新连接以恢复。',
      unavailable: 'LinkedIn 连接仅在欧盟、欧洲经济区和瑞士可用。',
      unknown: '暂时无法获取 LinkedIn 状态。',
      failed: '无法连接 LinkedIn，请重试。',
      fallbackHint: '截图方式仍然可用。',
      eligibilityHint:
        '仅在欧盟、欧洲经济区和瑞士可用。其他地区请使用“截图”标签——分析效果相同。',
      connect: '连接我的 LinkedIn',
      reconnect: '重新连接',
      disconnect: '断开连接',
    },
    optimisation: {
      v1: '版本 1（直击招聘官）',
      v2: '版本 2（关键词）',
      v3: '版本 3（差异化）',
    },
  },

  linkedinKit: {
    ready: '可复制的优化套件',
    readyHint: '点击某个板块的“复制”，或复制整套内容，然后粘贴到 LinkedIn。',
    copy: '复制',
    copied: '已复制',
    copyFailed: '失败',
    copyAll: '复制整套内容',
    kitCopied: '已复制整套内容',
    kitCopyFailed: '复制失败：请手动选择文字。',
    copyLabel: '复制：%{nom}',
    headline: '标题',
    about: '“简介”板块',
    aboutShort: '简短版',
    aboutFull: '完整版',
    experiences: '核心经历',
    skills: '技能',
    skillsPinned: '置顶技能（5 项）',
    skillsExtra: '补充技能',
    search: '求职偏好设置',
    searchTitles: '职位名称',
    searchContracts: '合同',
    searchLocations: '地点',
    searchSectors: '行业',
    searchAvailability: '到岗时间',
    express: '快速建议',
    photo: '头像',
    banner: '背景图',
    url: 'URL',
    education: '教育背景（可粘贴）',
    quality: '质量检查',
    keywordsAdded: '已添加的关键词',
    languagesRemoved: '已移除的语言（低于 B2）',
    skillsNotProven: '未体现的技能',
    toValidate: '待确认',
    removed: '已删除的内容',
    recommendations: '建议',
    recommended: '推荐',
    experienceN: '经历 %{n}',
    recoAsk: '可请求的推荐',
    recoAskWho: '可请求的推荐（%{who}）',
  },

  myLetter: {
    title: '我的求职信',
    subtitle: '创建你的求职信，然后让 AI 分析它。',
    pitchLead: '一封贴合目标职位的求职信，效果大不相同。',
    arguments: {
      analysed: '根据目标职位分析',
      skills: '突出你的技能',
      structure: '结构有说服力',
      ready: '可直接发送',
    },
    createSection: '创建我的求职信',
    createTitle: '创建求职信',
    createSubtitle: '基于你的一份简历',
    listSection: '我的求职信',
    listCount: '我的求职信 · %{count}',
    seeAll: '查看全部',
    newLetter: '新建',
    listSubtitle: '查找、打开和管理你的求职信',
    improveSection: '优化我的求职信',
    analyseTitle: '分析我的求职信',
    analyseSubtitle: '评分、发现的错误和优先事项',
    lastScore: '最近评分',
    lastScoreNone: '分析一封求职信，看看它的评分。',
    savedCount: {
      zero: '还没有保存的求职信',
      one: '已保存 %{count} 封求职信',
      other: '已保存 %{count} 封求职信',
    },
  },

  letterAnalyse: {
    importTitle: '导入我的求职信',
    importSubtitle: 'PDF / Word',
    importPicked: '已导入文件：%{name}',
    importRemove: '移除文件',
    orPaste: '或粘贴正文',
    rebuiltFromPdf: '正文根据导入的 PDF 还原。',
    title: '分析我的求职信',
    subtitle: 'AI 会对照目标职位为你的求职信评分，并告诉你最需要修改的地方。',
    resultTitre: '我的求职信分析',
    tabSaved: '我的求职信',
    tabImport: '导入',
    tabPaste: '粘贴',
    importHint: '支持 PDF 或 Word，将连同分析一起保存到“我的求职信”。',
    pasteLabel: '求职信正文',
    pastedTitle: '%{date} 粘贴的求职信',
    cvCrossedNone: '没有简历：信中陈述的事实不会被核实。',
    offerSub: '可选，让分析更精确',
    optionsTitre: '更多选项',
    optionsSousTitre: '求职者身份',
    profilRepris: '求职者身份来自你的账户。',
    sourceSection: '待分析的求职信',
    pickSaved: '选择已保存的求职信',
    pickSavedEmpty: '暂时还没有保存的求职信',
    pasteTitle: '粘贴求职信',
    pasteSubtitle: '在其他地方写好的求职信',
    pastePlaceholder: '在这里粘贴你的求职信正文…',
    pasteSaved: '你粘贴的求职信会连同分析结果一起保存到“我的求职信”。',
    titleLabel: '信件标题（可选）',
    titlePlaceholder: '例如：传播专员（工学交替）',
    offerLabel: '招聘启事',
    offerHint: '没有招聘启事时，分析将基于目标职业和市场要求。',
    offerPlaceholder: '在这里粘贴招聘启事…',
    profileLabel: '你的身份',
    profileHint: '它决定评判标准：初中生的求职信和高管的求职信不会用同一把尺子衡量。',
    metierLabel: '目标职位',
    metierPlaceholder: '例如：传播专员',
    submit: '分析我的求职信',
    loading: 'AI 正在分析你的求职信…',
    empty: '请添加要分析的求职信。',
    failed: '分析失败，请重试。',
    cvCrossed: '已与你的主简历交叉核对。',
    cvNotCrossed: '没有可用的简历：求职信中提到的事实未经核实。',
    obsolete: '这封求职信在分析后被修改过。评分针对的是之前的版本。',
    reanalyse: '重新分析',
    improve: '优化我的求职信',
    prioritiesSection: '优先修改',
    errorsSection: '发现的错误',
    strengthsSection: '已经做得好的地方',
    detailSection: '各项标准详情',
    coveredSection: '已满足的要求',
    missingSection: '未回应的要求',
    incoherenceSection: '与你简历的出入',
    faults: {
      zero: '未发现错误',
      one: '发现 %{count} 处错误',
      other: '发现 %{count} 处错误',
    },
    verdicts: {
      a_refaire: '需要重写',
      a_retravailler: '需要修改',
      envoyable_apres_corrections: '修改后可发送',
      solide: '扎实',
      excellente: '优秀',
    },
    criteres: {
      C1: '与职位的匹配度',
      C2: '证据与具体性',
      C3: '个性化',
      C4: '结构与篇幅',
      C5: '语言与文风',
      C6: '格式与礼仪',
    },
    niveaux: {
      insuffisant: '不足',
      fragile: '薄弱',
      solide: '扎实',
      excellent: '优秀',
    },
    gravites: {
      majeure: '严重',
      moyenne: '中等',
      mineure: '轻微',
    },
  },

  // « Améliorer ma lettre » (1.2.0).
  coverLetterImprove: {
    title: '优化我的求职信',
    blocked: '无法完成优化。',
    retry: '重试',
    reponseInvalide: '响应不完整，请稍后重试。',
    saveFailed: '无法保存。',
    copieTitre: '%{titre}（已优化）',
    tags: { hook: '开头', proof: '佐证', keywords: '关键词', clarity: '清晰度', closing: '结尾', subject: '主题' },
    modif: { objet: '求职信主题', paragraphe: '第 %{numero} 段', appliquer: '应用：%{nom}' },
    revue: {
      titre: {
        one: '建议修改 %{count} 处',
        other: '建议修改 %{count} 处',
      },
      aide: '逐条接受或拒绝修改。你现有的求职信不会改变：你的选择会生成一封新的求职信，并添加到“我的求职信”。',
      longueur: '正文字数：修改前 %{avant} 词，修改后 %{apres} 词。',
    },
    pied: {
      retenues: '已保留：%{count} / %{total}',
      appliquer: {
        one: '应用 %{count} 处修改',
        other: '应用 %{count} 处修改',
      },
      aucune: '未选择任何修改',
    },
    vide: { titre: '你的求职信已经很扎实', detail: 'AI 没有发现值得提出的修改。你的额度没有被扣除。' },
    fait: { titre: '新求职信已保存', detail: '优化后的求职信已保存在“我的求职信”中。你的原始求职信没有改变。', ouvrir: '打开求职信' },
  },

  letterList: {
    open: '打开',
    title: '我的求职信',
    subtitle: '查找、打开和管理你保存的求职信。',
    loadError: '无法加载你的求职信，请重试。',
    emptyTitle: '暂时还没有求职信。',
    emptyText: '基于你的一份简历生成第一封求职信，它会保存在这里。',
    emptyCta: '创建我的求职信',
    deleteTitle: '删除这封求职信？',
    deleteBody: '“%{title}”将被永久删除。',
  },

  letterCreate: {
    erreurFormat: '求职信未能生成。请重试：没有消耗任何额度。',
    modifierAnnonce: '修改招聘启事',
    change: '更换',
    changeA11y: '更换简历',
    optionsTitre: '更多选项',
    optionsSousTitre: '身份、机构、收件人、邮箱',
    reprisDuCompte: '求职者身份和申请类型来自你的账户。你在这里的选择会保留给下一封求职信。',
    failed: '求职信生成失败，请重试。',
    annonceOr: '或导入招聘启事文件',
    annonceFile: 'PDF / Word',
    annonceFilePicked: '已导入招聘启事：%{name}',
    annonceFileRemove: '移除文件',
    title: '生成求职信',
    subtitle: '选择一份简历，粘贴目标招聘启事，再调整语气。',
    resultTitle: '你的求职信',
    resultSubtitle: '通读一遍，核实需要核实的内容，然后下载。',
    noCvTitle: '你需要先有一份简历',
    noCvText:
      '求职信会根据你的简历提炼经历和证据。请先创建或导入一份简历，再回到这里。',
    goToCv: '前往我的简历',
    toConfirm: '发送前请核实',
    emailBlock: '附带邮件',
    download: '下载我的求职信',
    cvUsed: '使用的简历',
    annonce: '目标招聘启事',
    annoncePlaceholder: '在这里粘贴招聘启事…',
    profil: '身份',
    type: '申请类型',
    ton: '信件语气',
    langue: '求职信语言',
    organisation: '目标机构',
    organisationPlaceholder: '例如：ACME',
    metier: '目标职业',
    metierPlaceholder: '例如：项目专员',
    recipientName: '收件人——姓名',
    recipientNamePlaceholder: '不知道的话可以留空',
    recipientRole: '收件人——职务',
    recipientRolePlaceholder: '例如：招聘负责人',
    city: '你的城市',
    cityPlaceholder: '例如：Lyon',
    addEmail: '添加附带邮件',
    generate: '生成我的求职信',
    hint: '选择一份简历并添加招聘启事，即可开始生成。',
    defaultTitle: '求职信',
    saveFailed: '求职信已生成，但保存失败。',
    exportFailed: '导出失败。',
    wordCount: '%{count} 词',
    profils: {
      collegien: '初中生（初三实习）',
      lyceen: '高中生',
      etudiant_jeune_diplome: '在校生或应届毕业生',
      actif_confirme: '资深在职人士',
      manager_cadre_dirigeant: '管理者或高管',
      reconversion: '转行',
    },
    types: {
      reponse_offre: '回应职位招聘',
      candidature_spontanee: '主动求职',
      mobilite_interne: '内部调动',
    },
    tons: {
      classique: '经典',
      chaleureux: '热情',
      direct: '直接',
      entrepreneurial: '创业风格',
      institutionnel: '正式机构风格',
      executif: '高管风格',
    },
    langues: { francais: '法语', anglais: '英语' },
  },

  interview: {
    startFailed: '面试无法开始，请重试。',
    noCv: '你还没有简历。请先创建简历，再开始面试。',
    title: 'AI 面试',
    cvUsed: '使用的简历',
    change: '更换',
    changeA11y: '更换简历',
    hintEmpty: '填写目标职位后即可开始面试。',
    loadError: '无法加载。',
    addCv: '添加另一份简历',
    posteSection: '目标职位',
    posteFile: '添加职位描述',
    postePaste: '粘贴招聘信息',
    posteExample: '例如：市场营销专员',
    pickFile: 'PDF / Word',
    pickCaptures: '截图',
    offerPlaceholder: '在这里粘贴职位信息正文…',
    companyType: '企业类型（选填）',
    companyTypePlaceholder: '初创公司、大集团、事务所、中小企业…',
    adaptNote: 'AI 会分析目标职位并据此调整问题。',
    languageLabel: '面试语言',
    start: '开始面试',
    interlocutors: {
      recruiter: {
        label: '招聘官',
        points: { motivation: '动机', parcours: '经历', competences: '技能' },
      },
      manager: {
        label: '用人经理',
        points: { terrain: '实操', metiers: '专业', situation: '情景模拟' },
      },
    },
    quitTitle: '放弃这场面试？',
    quitBody: '你的进度将会丢失。',
    quitCancel: '继续面试',
    quitConfirm: '放弃',
    muteVoice: '关闭招聘官语音',
    voixActivee: '语音已开启',
    downloadPdf: '下载 PDF',
    pdfErreur: 'PDF 未能生成，请重试。',
    voixCoupee: '语音已关闭',
    voixReviendra: {
      lundi: '暂时使用手机语音：真人般的语音将在周一恢复。',
      mois: '暂时使用手机语音：真人般的语音将在下月1日恢复。',
      entretien: '本场面试剩余部分使用手机语音：真人般的语音将在下一场恢复。',
      bientot: '暂时使用手机语音：真人般的语音很快就会恢复。',
    },
    voixRestante: {
      semaine: '真人般的招聘官语音：本周还剩 %{n} 场面试。',
      semaineUn: '真人般的招聘官语音：本周还剩 1 场面试。',
      semaineAucun: '真人般的招聘官语音：本周次数已用完。周一之前将由手机语音代替。',
      mois: '真人般的招聘官语音：本月还剩 %{n} 场面试。',
      moisUn: '真人般的招聘官语音：本月还剩 1 场面试。',
      moisAucun: '真人般的招聘官语音：本月次数已用完。下月1日之前将由手机语音代替。',
    },
    unmuteVoice: '开启招聘官语音',
    listen: '收听',
    finish: '结束面试',
    answerPlaceholder: '你的回答…',
    stopDictation: '停止语音输入',
    startDictation: '语音回答',
    clearAnswer: '清除',
    send: '发送',
    strengths: '优势',
    improvements: '待改进',
    tips: '建议',
    restart: '重新开始',
    phasePresentation: '自我介绍阶段',
    phaseOngoing: '面试进行中',
    feedbackHeader: '你的反馈',
    overallLabel: '整体评价',
    dureeMin: '%{n} 分钟',
  },

  profile: {
    fallbackName: '你的资料',
    personalInfo: '个人信息',
    personalInfoSubtitle: '管理你的信息和偏好',
    subscription: '我的订阅',
    subscriptionSubtitle: '管理你的套餐和账单',
    activity: '我本月的使用情况',
    coaching: '我的辅导',
    coachingHint: '具体时间会通过邮件约定。',
    bookSession: '预约辅导',
    feedbackTitle: '有问题或改进建议想告诉我们吗？',
    feedbackSubtitle: '你的意见很重要！',
    avatarSheet: '头像照片或背景',
    importFailed: '导入失败，请重试。',
    stats: {
      cvCreated: '已创建简历',
      interviews: 'AI 面试',
      cvScores: '简历评分',
      linkedinScores: 'LinkedIn 评分',
    },
    noSession: '暂无已安排的辅导',
    sessionFallback: '辅导',
    lastSession: '最近一次辅导预约于 %{date}——%{formule}',
  },

  personalInfo: {
    firstName: '名',
    firstNamePlaceholder: '你的名',
    lastName: '姓',
    lastNamePlaceholder: '你的姓',
    email: '联系邮箱',
    emailPlaceholder: 'you@example.com',
    phone: '电话',
    phonePlaceholder: '06 12 34 56 78',
    address: '地址 / 城市',
    addressPlaceholder: '例如：Paris',
    linkedin: 'LinkedIn 链接',
    linkedinPlaceholder: 'https://www.linkedin.com/in/…',
  },

  profileCompletion: {
    title: '还差一步',
    birthDatePlaceholder: 'DD/MM/YYYY',
    continue: '继续',
    later: '稍后',
    saveFailed: '无法保存。',
  },

  notifications_screen: {
    title: '通知',
    empty: '暂时没有新通知。',
    settingsSubtitle: '你的提醒，以及推进求职的小技巧。',
    tips: '小技巧',
  },

  auth: {
    signup: {
      title: '创建账号，',
      titleAccent: '开始行动。',
      subtitle: '优化简历，准备面试，向你的合同迈进。',
      cta: '创建我的账号',
    },
    signupEtablissement: {
      title: '创建您的',
      titleAccent: '院校账户',
      subtitle: '并为您的学生提供准备求职申请的工具。',
    },
    signupPartenaire: {
      title: '创建您的',
      titleAccent: '合作伙伴账户',
      subtitle: '并为您陪伴的年轻人提供准备求职申请的工具。',
    },
    signin: {
      title: '继续',
      titleAccent: '脱颖而出。',
      subtitle: '接着完善你的内容，向下一场面试迈进。',
      cta: '登录',
    },
    pending:
      '如果这个邮箱尚未被使用，你将收到一封确认邮件。记得检查垃圾邮件箱。',
    invalidEmail: '邮箱地址无效。',
    invalidPassword: '密码无效。',
    emailLabel: '邮箱',
    emailPlaceholder: 'name@email.com',
    passwordLabel: '密码',
    hidePassword: '隐藏密码',
    showPassword: '显示密码',
    tabSignin: '登录',
    tabSignup: '创建账号',
    forgot: {
      link: '忘记密码？',
      title: '找回你的',
      titleAccent: '账号。',
      subtitle: '输入你的邮箱地址，我们会发送一个链接，让你设置新密码。',
      cta: '发送链接',
      sent: '如果该邮箱对应的账号存在，我们已发送一封邮件，里面有设置新密码的链接。请检查垃圾邮件，并在这部手机上打开链接。',
      back: '返回登录',
    },
    newPassword: {
      title: '设置你的',
      titleAccent: '新密码。',
      subtitle: '它将取代旧密码。',
      newLabel: '新密码',
      confirmLabel: '确认密码',
      tooShort: '密码至少需要 %{min} 个字符。',
      mismatch: '两次输入的密码不一致。',
      cta: '保存',
      cancel: '取消并退出登录',
    },
    or: '或',
    google: '使用 Google 继续',

    error: {
      invalidCredentials: '邮箱或密码错误。',
      emailNotConfirmed:
        '你的账号尚未确认。请打开我们通过邮件发给你的链接——记得检查垃圾邮件箱。',
      emailInvalid: '这个邮箱地址无效。',
      validationFailed: '请检查你的邮箱和密码。',
      weakPassword: '密码强度太低。请设置更长的密码，并包含数字和字母。',
      rateLimit: '尝试次数过多。请几分钟后再试。',
      userBanned: '该账号已被暂停使用。请联系我们了解详情。',
      network: '无法连接。请检查网络连接后重试。',
      confirmExpired:
        '该确认链接已失效。请用同一个邮箱重新注册，以获取新的链接。',
      confirmWrongDevice: '请在你创建账号的那台手机上打开此链接。',
      resetExpired: '此重置链接已失效。请通过“忘记密码？”重新申请。',
      resetWrongDevice: '请在你申请重置的那台手机上，打开收到的最新一封邮件。',
      samePassword: '请选择与旧密码不同的密码。',

      serverSignup:
        '暂时无法创建你的账号——问题出在我们这边，不是你的原因。请几分钟后再试，或使用 %{providers} 继续。',
      serverSignin:
        '暂时无法为你登录——问题出在我们这边，不是你的原因。请几分钟后再试，或使用 %{providers} 继续。',
      serverOauth:
        '登录未成功——问题出在我们这边，不是你的原因。请几分钟后再试，或使用邮箱和密码登录。',
      popupBlocked:
        '你的浏览器拦截了 Google 登录窗口。请允许此网站的弹出窗口，然后重新登录——或使用邮箱和密码登录。',
      appleUnknown:
        '使用 Apple 登录未成功。请确认你已在这台设备上登录 iCloud，并且你的 Apple ID 已开启双重认证。否则，请使用邮箱和密码登录。',
      serverSignout: '退出登录未成功。请几分钟后再试。',
      serverConfirm:
        '暂时无法确认你的账号——问题出在我们这边，不是你的原因。请几分钟后再试，或使用 %{providers} 继续。',
      serverReset:
        '暂时无法重置你的密码——问题出在我们这边，不是你的原因。请几分钟后再试。',

      googleStart: '暂时无法打开 Google 登录。请几分钟后再试。',
      googleResponse: 'Google 返回的信息不完整，请重试。',
      appleResponse: 'Apple 返回的信息不完整，请重试。',
      deleteFailed:
        '你的账号未被删除：操作在删除任何内容之前就失败了，你的数据完好无损。请几分钟后再试。',
      deleteAppleCancelled: '已取消删除：请通过 Apple 确认以删除你的账号。',

      providersIos: 'Google 或 Apple',
      providersAndroid: 'Google',
    },
  },

  onboarding: {
    highlights: { cv: '简历', linkedin: 'LinkedIn', interview: '面试', coach: '辅导' },
    title: '你的履历值得',
    mots: {
      reponses: '被回应。',
      cv: '一份过关的简历。',
      entretien: '一次面试。',
      contrat: '一份合同。',
    },
    sousTitre: '从简历到签下合同，我们一路陪你。',
    wheel: {
      cv: 'ATS 简历',
      linkedin: 'LinkedIn',
      interview: 'AI 面试',
      coach: '专家辅导',
      offers: '职位',
      letter: '求职信',
      center: '你的合同',
    },
    quickSignup: '快速注册',
    dataProtected: '数据受保护',
    createAccount: '创建我的账号',
    haveAccount: '我已有账号',
  },


  acceptTerms: {
    titleOutdated: '我们的条款已更新',
    titleFirst: '就快完成了 ✨',
    changedPrefix: '我们的',
    changedTermsLink: '使用条款',
    changedMiddle: '和',
    changedPrivacyLink: '隐私政策',
    changedSuffix:
      '已更新（版本 %{version}）。请重新阅读并接受，以便继续使用本应用。',
    firstPrefix: '我们简化了条款，以便更好地',
    firstAccent: '保护你的数据',
    pillars: {
      data: '数据\n受保护',
      transparency: '完全\n透明',
      consent: '未经你同意\n不做任何操作',
    },
    docTerms: '使用条款',
    docPrivacy: '隐私政策',
    accept: '继续',
    refuseOutdated: '拒绝并退出登录',
    refuseFirst: '稍后再说，先退出登录',
    help: '需要帮助？',
    deleteAccount: '删除我的账号',
    saveFailed: '无法保存，请重试。',
  },

  legalHeaders: {
    documentsEnFrancais: '文件仅提供法语版本',
    privacy: '隐私与数据',
    terms: '使用条款',
    dataDeletion: '删除账号',
  },

  plans: {
    free: {
      tagline: '入门之选',
      price: '免费',
      period: '永久',
      entretiens: '每月 1 场 AI 面试，配有自然的招聘官语音',
      features: [
        '每月 1 次简历评分和 1 次简历导入',
        '每月 1 次 LinkedIn 诊断',
        '每月 1 封求职信和 1 次求职信分析',
        '每月 1 次 PDF 导出',
        '不含 AI 创建简历',
        '基础模板',
      ],
      featuresOffres: ['工学交替和工作职位：免费浏览', '每月 2 次 AI 申请（“职位”页面）'],
    },
    plus: {
      tagline: '均衡之选',
      period: '/ 月',
      entretiens: '每月 8 场 AI 面试',
      voix: '每周 2 场面试使用真人般的招聘官语音',
      features: [
        '每月 5 次简历创建和 5 次导入',
        '每月 5 次简历评分',
        '每月 5 次 LinkedIn 诊断',
        '每月 5 封求职信和 5 次求职信分析',
        '每月 20 次 AI 段落改写',
        '无限导出，全部模板',
      ],
      featuresOffres: ['每月 15 次 AI 申请（“职位”页面）'],
    },
    pro: {
      tagline: '助你求职一路到底',
      period: '/ 月',
      entretiens: '每月 20 场 AI 面试',
      voix: '每周 5 场面试使用真人般的招聘官语音',
      ecart: '每月 20 场面试和 60 次 AI 申请',
      ecartSansOffres: '每月 20 场 AI 面试',
      features: [
        '无限次：简历、评分、LinkedIn 诊断、求职信和分析',
        '无限次 AI 改写和导出',
        '高级模板',
      ],
      featuresOffres: ['每月 60 次 AI 申请（“职位”页面）'],
    },
    school: {
      features: [
        '全部功能，面向所有学生',
        '适合学校和学徒培训中心（CFA）',
        '按席位计费',
        '授权许可',
        '机构跟踪看板',
        '季度报告',
        '个性化定制',
      ],
    },
  },

  plansExtra: {
    proAnnual: {
      name: 'Pro 年付',
      period: '/年',
      badge: '−%{pourcentage}%',
      equivalent: '相当于 %{monthly}/月 × 12',
      cta: '选择年付',
    },
  },

  paywall: {
    mentionAbonnement: '按所选套餐按月或按年订阅，到期自动续订相同周期，除非在当前周期结束前至少 24 小时取消。你可以随时在 App Store 或 Google Play 的订阅管理中取消；取消将在当前周期结束时生效。',
    tonPlan: '你的套餐',
    bientotSurPlateforme: '此平台即将开放订阅。',
    currentPlan: '你当前的套餐：',
    title: '更上一层楼',
    capReached: '你本月的使用次数已用完。额度将于 %{date} 重置。',
    notIncluded: '你当前的套餐不包含此功能。',
    tabIndividual: '个人',
    tabSchools: '学校',
    restore: '恢复购买',
    contact: '联系我们',
    recommended: '推荐',
    subscribe: '订阅',
    pendingApproval: '购买正在等待批准。',
    prixIndisponibles: '目前无法显示价格，请稍后重试。',
    genericError: '出错了。',
    activated: '订阅已开通 🎉',
    restored: '购买已恢复。',
    activating: '已收到购买。正在开通你的订阅…',
    activationEnCours: '已收到购买。你的订阅正在开通，可能需要片刻。',
    restoreEnCours: '已发送恢复请求。如果你的账户关联了订阅，稍后就会显示。',
    schoolMailSubject: 'Offre établissement Horméia',
  },

  coaching: {
    tarifsIndisponibles: '价格暂时无法显示。',
    reessayer: '重试',
    montantIncoherent:
      '付款暂时不可用：显示的价格与将要扣除的金额不一致。在问题解决之前，我们不会扣任何钱。',
    retourSuccesTitre: '付款已确认',
    retourSuccesCorps:
      '你的付款已到账。你将收到一封确认邮件，内含本次辅导的详细信息。',
    retourEnCoursTitre: '付款处理中',
    retourEnCoursCorps:
      '你的银行尚未确认这笔付款，使用某些支付方式时这很正常。请不要重新付款：否则会被扣两次。付款确认后你会立即收到邮件。',
    retourEchecTitre: '付款被拒',
    retourEchecCorps:
      '付款未成功，你没有被扣款。你可以使用同一种或其他支付方式重试。',
    retourAnnuleTitre: '付款已取消',
    retourAnnuleCorps: '你中断了付款，没有被扣款。你的时段仍然可以预约。',
    retourVersCoaching: '返回辅导',
    title: '辅导',
    pickSlot: '选择我的时段',
    soon: '即将上线',
    slotBeforePayment: '你的时段会在付款前确认。',
    howTitle: '流程说明',
    step1: '你选择时段',
    step2: '你确认预订',
    step3: '你支付本次辅导',
    pickFormule: '选择你的方案',
    formuleNotFound: '找不到该方案。请返回并选择一个方案。',
    reserve: '预订',
    checkingSlot: '正在与日程核对你的时段…',
    slotConfirmed: '你的时段已确认，只差确认付款了。',
    chosenFormule: '已选方案',
    email: '邮箱',
    searching: '你在寻找什么（选填）',
    payUnavailable:
      '支付暂时不可用，请稍后再试。',
    paid: '付款成功 ✓。你的预约已确认，Julien 会再联系你。',
    processing: '你的付款正在处理中。请勿重复付款，验证完成后你会收到确认。',
    securePayment: '银行卡安全支付（Stripe）。只会扣款一次。',
    nameRequired: '请填写你的姓名。',
    invalidEmail: '邮箱地址无效。',
    paymentFailed: '无法付款，请重试。',
    paymentFailedShort: '无法付款。',
  },

  cvCreateFlow: {
    titre: '创建简历',
    etapeSur: '第 %{n} 步，共 %{total} 步',
    step1Titre: '你想应聘什么职位？',
    step1Sous: 'AI 会围绕这个职位调整整份简历。',
    step2Titre: '你的经历',
    step2Sous: '不用写得多好，AI 会帮你润色。',
    step3Titre: '最后一步',
    step3Sous: '全部为选填，现在就可以生成简历。',
    nameFromProfile: '来自你的个人资料',
    importTitre: '已经有简历了？',
    importSous: '直接导入，更快。',
    roleRequired: '请先填写目标职位再继续。',
    tuileProjet: '一个项目',
    tuileAutre: '其他内容',
    optionnel: '选填',
    ajoute: '已添加',
    nameLabel: '简历上的姓名',
    namePlaceholder: '例如：Jean Dupont',
    languageLabel: '简历语言',
    languageHint: '你可以用法语回答，生成英文简历，反之亦可。',
    contratLabel: '你在找…',
    contratHint: '可不选。再次点击即可取消。',
    contrats: { alternance: '工学交替', stage: '实习', cdi: 'CDI（无固定期限合同）', cdd: 'CDD（固定期限合同）' },
    generate: '生成我的简历',
    next: '继续',
    buildingTitle: '正在为你制作简历。',
    buildingHint: '几秒钟就好，保证。',
    editTitle: '编辑我的简历',
    cancel: '取消',
    save: '保存',
    readyTitle: '你的简历准备好了',
    atsScore: 'ATS 评分',
    edit: '编辑',
    saving: '保存中…',
    retrySave: '重新保存',
    download: '导出我的简历',
    condensePourUnePage: '内容已精简为一页',
    aiAdvice: 'AI 建议',
    targetOffer: '针对某个职位（选填）',
    offerPlaceholder: '在这里粘贴职位信息…',
    offerFile: '职位（PDF/Word）',
    offerCaptures: '截图',
    recommendCta: '针对该职位的建议',
    recommendSection: '针对该职位的建议',
    testInScore: '在“简历评分”中测试这份简历',
    skillPlaceholder: '例如：SQL',
    interestPlaceholder: '例如：越野跑',
    noterCe: '给这份简历评分',
    savedIn: '已保存到“我的简历”',
    editsSaved: '修改已保存',
    saveFailed: '简历已生成，但保存失败。',
    editSaveFailed: '无法保存。',
    offerHint: '添加一份职位描述：AI 会建议如何针对该职位突出你的经历（不会编造任何内容）。是否采用由你通过编辑来决定。',
  },

  help: {
    title: '帮助与联系',
    faqSection: '常见问题',
    contactSection: '联系我们',
    writeUs: '给我们写信',
    sendEmail: '发送邮件',
    mailSubject: 'Aide — Horméia',
    faq: {
      ats: {
        q: '我的 ATS 评分是怎么计算的？',
        a: 'AI 会按照简历筛选软件（ATS）看重的标准分析你的简历：是否包含职位关键词、结构是否清晰、板块是否规范、成果是否量化。评分反映的就是这种兼容性，并附有具体的改进建议。',
      },
      privacy: {
        q: '我的数据会保密吗？',
        a: '你的简历和信息用于实现应用的各项功能（评分、创建、面试）。专门介绍隐私和数据管理的页面即将发布；在此之前，如有任何问题，欢迎给我们写信。',
      },
      coaching: {
        q: '辅导是怎么进行的？',
        a: '辅导是由人力资源专家 Julien Dauzats 提供的真人辅导服务，与 AI 功能不同。你选择一个方案，在应用内预约，然后通过视频进行交流。',
      },
    },
  },

  misc: {
    personalInfoTitle: '个人信息',
    save: '保存',
    coachNamePlaceholder: '你的姓名',
    coachGoalPlaceholder: '例如：准备下周的数据岗位面试',
    atsCompatible: '兼容 ATS',
    completeProfile: '完善你的资料',
    completeProfileCta: '完善我的资料',
    later: '稍后',
    feedbackQuestion: '这个结果对你有帮助吗？',
    send: '发送',
    importPhoto: '导入照片',
    orColour: '或选择背景色',
    add: '添加',
    yearPlaceholder: '年份',
    comingSoon: '即将上线。',
  },

  progress: {
    linkedinKitSections: [
      '正在撰写你的标题…',
      '正在撰写你的“简介”板块…',
      '正在改写你的经历…',
      '正在挑选你的技能…',
      '正在设置你的求职偏好…',
      '正在准备你的快速建议…',
      '正在完成你的工具包…',
    ],

    linkedinSections: [
      '正在阅读你的主页…',
      '正在找出常见错误…',
      '正在准备你的评分和优先事项…',
    ],

    elapsed: {
      start: '正在为你制作简历…',
      mid: '再等几秒…',
      long: '比平时稍久一些，我们还在继续。',
    },
    letter: {
      start: '正在撰写你的求职信…',
      mid: '再等几秒…',
      long: '比平时稍久一些，我们还在继续。',
    },
    linkedin: {
      start: '正在分析你的主页…',
      mid: '再等几秒…',
      long: '比平时稍久一些，我们还在继续。',
    },
    improve: {
      start: '正在优化你的简历…',
      mid: '再等几秒…',
      long: '比平时稍久一些，我们还在继续。',
    },
    improveLetter: {
      start: '正在优化你的求职信…',
      mid: '再等几秒…',
      long: '比平时稍久一些，我们还在继续。',
    },
    received: '已收到',
    saved: '已保存',
  },

  // Licence accordée par l'établissement de l'étudiant (lot 4 du back-office). Aucun lien d'achat ici.
  licenceEcole: {
    title: '学校许可',
    loading: '加载中…',
    loadError: '无法加载你的许可。请重试。',
    retry: '重试',
    intro: '学校给你发了邀请码吗？输入邀请码即可激活你的许可。',
    codeLabel: '邀请码',
    codePlaceholder: 'XXXX-XXXX-XXXX',
    activer: '激活我的许可',
    activating: '正在激活…',
    voitTitre: '学校能看到什么',
    voit: [
      '你的状态（求职中、已签约、合同终止）和关键日期，由学校录入。',
      '学校为你所做的辅导记录：电话、会面、转发的职位。',
      '你在应用中的活动：你使用应用的日期。许可一经激活即可见，因为许可由你的学校提供。',
    ],
    neVoitPas: '学校永远看不到你的简历、求职信或面试的内容。',
    active: '许可已生效',
    via: '由 %{nom} 提供',
    employeurTitre: '你的企业',
    employeurContrat: '学校已将你与 %{nom} 关联，自 %{date} 起。',
    employeurVoitTitre: '如果你允许，企业能看到什么',
    employeurVoit: [
      '你的姓名、专业、学校和合同日期。',
      '你的状态（求职中、已签约、合同终止）。',
    ],
    employeurNeVoitPas: '企业永远看不到你的电子邮箱、学校的辅导记录、你的求职申请、你在应用中的活动，也看不到你的简历、求职信或面试的内容。',
    employeurConsentTitre: '与我的企业分享我的状态',
    employeurConsentBody: '默认关闭。你可以随时撤回，你的合同和许可不会改变。学校只能看到你是否同意。',
    employeurConsentError: '无法保存你的选择。请重试。',
    succes: '你的 %{nom} 许可已激活。',
    erreurs: {
      code_invalide: '此邀请码无效。请检查，或向学校索取新的邀请码。',
      invitation_expiree: '此邀请已过期。请向学校索取新的邀请。',
      abonnement_actif: '你已有有效订阅。请在 App Store 或 Google Play 设置中取消后再试。',
      plus_de_sieges: '你的学校已没有可用名额。请联系学校。',
      organisation_inactive: '你的学校许可目前未生效。请联系学校。',
      inconnu: '目前无法激活。请重试。',
    },
  },

  settings: {
    title: '设置',
    sections: {
      account: '账号',
      preferences: '偏好',
      admin: '管理',
      danger: '危险操作',
    },
    privacy: '隐私与数据',
    licenceEcole: '学校许可',
    dataDeletion: '删除账号和数据',
    notifications: '通知',
    adminDashboard: '管理后台',
    signOut: '退出登录',
    deleteAccount: '删除我的账号',
    deleting: '正在删除…',
    deleteFailedTitle: '无法删除',
    deleteConfirmTitle: '删除你的账号？',
    deleteConfirmBody: '你的账号将立即停用，数据将在 29 天后被永久删除。你的订阅不会自动取消：请在 App Store 或 Google Play 的设置中取消。',
    language: {
      label: '应用语言',
      hint: '你生成的文档会保留各自的语言，即创建时选择的语言。',
      relanceTitre: '重启应用？',
      relanceTexte: '切换到这种语言需要重启 Horméia。已保存的简历、求职信和设置都会保留；正在进行的面试或未保存的内容将会丢失。',
      relanceOk: '重启',
    },
  },
  update: {
    banner: {
      title: '有新版本可用',
      body: '更新 Horméia，体验最新改进。',
      action: '立即更新',
      dismiss: '稍后',
      dismissA11y: '隐藏此更新提示',
    },
    blocking: {
      title: '此版本已不再兼容',
      body: '自从安装这个版本以来，Horméia 已经有了变化，这个版本已无法与服务正常配合。这不是故障，你也不需要做任何修复。',
      reassurance: '你的数据和文档都完好无损：打开新版本后就能看到它们。',
      action: '安装更新',
    },
  },
  quota: {
    resetDate: '%{monthYear}1日',
    capReachedNoUpgrade:
      '你已达到当前套餐对此功能的使用上限。额度将于 %{date} 重置。',
    notIncludedNoUpgrade: '你的授权许可不包含此功能。',
  },
  errors: {
    network: '无法连接。请检查网络连接后重试。',
    server: '服务暂时不可用，请稍后再试。',
    aiBadOutput: 'AI 的回答无法使用，请重新分析。',
    profilTropEloigne: '该职位与你的简历差距太大，无法写出可信的求职信。',
    invalidRequest: '请检查填写的信息后重试。',
    ficheTropLongue: '职位描述太长：最多 20 页或 10000 个字符。',
    unauthorized: '你的登录已过期，请重新登录。',
  },

  nouveautes: {
    titre: 'Horméia 新功能',
    offresTitre: '工学交替职位',
    offresTexte: '找到符合你个人情况的工学交替和工作职位，然后用 AI 准备你的申请。',
    voirOffres: '查看职位',
    voixTitre: '招聘官语音',
    voixTexte: '面试模拟现在有了真人般的语音。根据你的套餐：Free 每月 1 场面试，Accélérateur 或 École 每周 2 场，Pro 每周 5 场。',
    languesTitre: '新增语言',
    languesTexte: 'Horméia 现已支持西班牙语、葡萄牙语、中文和阿拉伯语。你可以在设置中更改语言。',
    politiqueTitre: '隐私政策',
    politiqueTexte: '我们更新了隐私政策，说明我们如何衡量应用的使用情况。你可以随时在设置中查看。',
    voirPolitique: '阅读隐私政策',
    bouton: '开始吧',
  },
};
